import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Button,
  Headline,
  IconChevron,
  InputField,
  Link,
  Text,
  THEME,
  ValidationMessage,
} from "../migration-wrappers";
import { MAX_WIDTH } from "../../styles";
import Logo from "../../assets/images/dhl-logo.svg";

// #region Component Styles
const Container = styled.div`
  display: flex;
  margin-bottom: calc(${THEME.getUnit("large")} * 2);
  margin: auto;
  max-width: ${MAX_WIDTH};
  padding: 0 ${THEME.getUnit("large")};
`;

const SignInForm = styled.form`
  align-self: center;
  flex-shrink: 0;
  max-width: 500px;
  padding: calc(${THEME.getUnit("large")} * 2);
  width: 100%;

  :before,
  :after {
    background-color: ${THEME.getGrayColor("200")};
    border-radius: ${THEME.getRadius()};
    content: " ";
    display: block;
    height: 4px;
    margin: 0 auto calc(${THEME.getUnit("large")} * 2) auto;
    width: 50px;
  }

  :after {
    margin: calc(${THEME.getUnit("regular")} * 2) auto 0 auto;
  }
`;

const Title = styled(Headline).attrs({
  tag: "h2",
})`
  & h2 {
    font-size: 1.375rem;
    @media (min-width: 768px) {
      font-size: 2.25rem;
      line-height: 35px;
    }
  }
`;

const Instructions = styled(Text)`
  span {
    color: ${THEME.getGrayColor("600")};
    margin: ${THEME.getUnit("large")} 0 ${THEME.getUnit("regular")} 0;
    text-align: left;
    display: block;
  }
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.getUnit("small")};
`;

const FormField = styled(InputField)`
  margin: ${THEME.getUnit("small")} 0 !important;
  text-align: left;
`;

const PasswordField = styled(FormField)`
  text-align: left;
`;

const SignInButton = styled(Button)`
  button {
    width: 100% !important;
    margin-bottom: ${THEME.getUnit("regular")};
    margin-top: ${THEME.getUnit("large")};
  }
`;

const InvalidCredentials = styled(ValidationMessage)`
  & * {
    color: ${THEME.getPrimaryColor("400")};
    justify-content: center;
    margin-top: ${THEME.getUnit("small")};
    text-align: center;
  }
`;

const BackHomepage = styled(Link)`
  a {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  a span {
    display: flex;
    text-decoration: none !important;
    justify-content: flex-end;
    align-items: center;
    font-weight: 400;
  }
`;

const BackIcon = styled(IconChevron)`
  margin-right: 3px;
  height: 14px;
  display: flex;
`;

const Hero = styled.div`
  background-color: ${THEME.getSecondaryColor("400")};
  box-sizing: border-box;
  flex: 1 0 0;
  height: 100vh;
  padding: calc(${THEME.getUnit("large")} * 2);
  text-align: right;
`;

const DHLLogo = styled(Logo)`
  height: 40px;
`;
// #endregion

const getEmailPlaceholder = () =>
  Math.random() < 0.5 ? "john.doe@dhl.com" : "mary.jane@dhl.com";

const SignIn = ({ isAuthenticated, signInError, onSignIn, onSignInReset }) => {
  const location = useLocation();
  const usernameRef = useRef();
  const placeholderRef = useRef(getEmailPlaceholder());

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [hasTriedToSignIn, setHasTriedToSignIn] = useState(false);

  useEffect(() => {
    onSignInReset();
  }, []);

  useEffect(() => usernameRef?.current?.focus(), []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setHasTriedToSignIn(true);
    onSignIn({ username, password });
  };

  if (isAuthenticated) {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  return (
    <Container>
      <SignInForm onSubmit={handleSubmit}>
        <Title>Sign in to DCI Hub</Title>
        <Instructions>Please enter your credentials below</Instructions>
        <FieldsWrapper>
          <FormField
            id="login-email"
            onChange={(e) => setUsername(e.target.value)}
            ref={usernameRef}
            value={username}
            label="Email"
            placeholder={placeholderRef.current}
          />
          <PasswordField
            id="login-password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            label="Password"
            placeholder="Add your password"
            labelType="static"
            type="password"
          />

          <SignInButton type={Button.SUBMIT}>Sign in</SignInButton>

          {hasTriedToSignIn && signInError && (
            <InvalidCredentials
              variant={ValidationMessage.INVALID}
              message="Invalid username and password"
            />
          )}

          <BackHomepage
            href={(location.state && location.state.redirectUrl) || "/"}
          >
            <BackIcon direction="left" /> Back to DCI Hub
          </BackHomepage>
        </FieldsWrapper>
      </SignInForm>

      <Hero>
        <DHLLogo />
      </Hero>
    </Container>
  );
};

SignIn.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  signInError: PropTypes.string,
  onSignIn: PropTypes.func.isRequired,
  onSignInReset: PropTypes.func.isRequired,
};

export default SignIn;
